/**
 * Theme: "system" follows the OS, "dark" / "light" pin it. The choice lives
 * in localStorage and is applied as the `dark` class on <html>, which is
 * what the Tailwind tokens key on. index.html ships with class="dark" so
 * the first paint is never a white flash for the (default) dark users.
 */
export type Theme = "system" | "dark" | "light"

const KEY = "lucy.web.theme"
const listeners = new Set<(t: Theme) => void>()
let mq: MediaQueryList | null = null

export function getTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY)
    if (v === "dark" || v === "light" || v === "system") return v
  } catch { /* storage blocked */ }
  return "system"
}

export function resolvedTheme(t: Theme = getTheme()): "dark" | "light" {
  if (t === "dark" || t === "light") return t
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark"
}

export function applyTheme(t: Theme = getTheme()): void {
  const mode = resolvedTheme(t)
  const root = document.documentElement
  root.classList.toggle("dark", mode === "dark")
  root.style.colorScheme = mode
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute("content", mode === "dark" ? "#120C21" : "#F7F3FB")
}

export function setTheme(t: Theme): void {
  try { localStorage.setItem(KEY, t) } catch { /* storage blocked */ }
  applyTheme(t)
  for (const fn of listeners) fn(t)
}

export function onThemeChange(fn: (t: Theme) => void): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

/** Call once at startup: applies the stored choice and follows the OS while on "system". */
export function initTheme(): void {
  applyTheme()
  if (!window.matchMedia) return
  mq = window.matchMedia("(prefers-color-scheme: light)")
  const follow = () => { if (getTheme() === "system") applyTheme("system") }
  if (typeof mq.addEventListener === "function") mq.addEventListener("change", follow)
  else mq.addListener(follow)
}
